// src/pages/admin/AnalyticsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { fmtDateUS } from '../../utils/dates';

const pad2 = (n) => String(n).padStart(2, '0');
// Local calendar date (toISOString would shift the day near midnight UTC).
const fmtDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const isoToUS = (iso) => {
  const [y, m, d] = (iso || '').split('-');
  return y && m && d ? `${m}/${d}/${y}` : '';
};

// "MM/DD/YYYY" -> "YYYY-MM-DD"; null if not a real calendar date.
const usToISO = (us) => {
  const m = (us || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [, mm, dd, yyyy] = m;
  const probe = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (
    probe.getFullYear() !== Number(yyyy) ||
    probe.getMonth() !== Number(mm) - 1 ||
    probe.getDate() !== Number(dd)
  ) {
    return null;
  }
  return `${yyyy}-${mm}-${dd}`;
};

// Keep only digits and re-insert slashes: 07152026 -> 07/15/2026.
const maskUS = (v) => {
  const digits = v.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

function presetRange(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return { start: fmtDate(start), end: fmtDate(end) };
}

// Current tax season starts July 1 (of this year if we're past it, else last year).
function seasonRange() {
  const now = new Date();
  const year = now.getMonth() + 1 >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  return { start: `${year}-07-01`, end: fmtDate(now) };
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className={`border rounded p-4 ${accent || 'bg-gray-50'}`}>
      <div className="text-sm text-gray-600">{label}</div>
      <div className="text-3xl font-semibold text-[#0F2C5E]">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function AnalyticsTab() {
  const initial = presetRange(30);
  const [range, setRange] = useState(initial);
  // What the two MM/DD/YYYY inputs currently show (may be mid-typing).
  const [draft, setDraft] = useState({ start: isoToUS(initial.start), end: isoToUS(initial.end) });
  const [rangeError, setRangeError] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token');

  const fetchAnalytics = useCallback(async (r) => {
    try {
      setLoading(true);
      setError(null);
      const qs = new URLSearchParams({ start: r.start, end: r.end }).toString();
      const resp = await fetch(`/api/dashboard/analytics/?${qs}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!resp.ok) throw new Error(`Error loading analytics (${resp.status})`);
      setData(await resp.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAnalytics(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (r) => {
    setRange(r);
    setDraft({ start: isoToUS(r.start), end: isoToUS(r.end) });
    setRangeError(null);
    fetchAnalytics(r);
  };

  const exportExcel = async () => {
    try {
      const qs = new URLSearchParams({ start: range.start, end: range.end }).toString();
      const resp = await fetch(`/api/dashboard/analytics/export/?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_${range.start}_${range.end}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    }
  };

  const applyDraft = (e) => {
    e.preventDefault();
    const start = usToISO(draft.start);
    const end = usToISO(draft.end);
    if (!start || !end) {
      setRangeError('Enter dates as MM/DD/YYYY.');
      return;
    }
    if (start > end) {
      setRangeError('"From" must be on or before "To".');
      return;
    }
    setRangeError(null);
    const r = { start, end };
    setRange(r);
    fetchAnalytics(r);
  };

  const t = data?.totals || {};
  const breakdown = t.filed_status_breakdown || {};
  const breakdownStr = ['complete', 'send', 'prefiled', 'rejected']
    .filter((k) => breakdown[k])
    .map((k) => `${k}: ${breakdown[k]}`)
    .join('  ·  ');

  return (
    <div className="bg-white p-6 rounded shadow space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h3 className="text-2xl font-semibold">Analytics</h3>

        {/* Period controls */}
        <div>
          <form onSubmit={applyDraft} className="flex flex-wrap items-end gap-2">
            <div>
              <label htmlFor="analyticsFrom" className="block text-xs text-gray-600 mb-1">From</label>
              <input
                id="analyticsFrom"
                type="text"
                inputMode="numeric"
                placeholder="MM/DD/YYYY"
                maxLength={10}
                value={draft.start}
                onChange={(e) => setDraft((d) => ({ ...d, start: maskUS(e.target.value) }))}
                className="border p-1.5 text-sm w-28"
              />
            </div>
            <div>
              <label htmlFor="analyticsTo" className="block text-xs text-gray-600 mb-1">To</label>
              <input
                id="analyticsTo"
                type="text"
                inputMode="numeric"
                placeholder="MM/DD/YYYY"
                maxLength={10}
                value={draft.end}
                onChange={(e) => setDraft((d) => ({ ...d, end: maskUS(e.target.value) }))}
                className="border p-1.5 text-sm w-28"
              />
            </div>
            <button
              type="submit"
              className="bg-[#0F2C5E] text-white px-4 py-1.5 text-sm hover:bg-[#0F2C5E]/80"
            >
              Apply
            </button>
            <div className="flex gap-1">
              <button type="button" onClick={() => applyPreset(presetRange(7))} className="px-2 py-1.5 text-xs bg-gray-200 hover:bg-gray-300">7d</button>
              <button type="button" onClick={() => applyPreset(presetRange(30))} className="px-2 py-1.5 text-xs bg-gray-200 hover:bg-gray-300">30d</button>
              <button type="button" onClick={() => applyPreset(seasonRange())} className="px-2 py-1.5 text-xs bg-gray-200 hover:bg-gray-300">Season</button>
              <button type="button" onClick={() => applyPreset({ start: '2020-01-01', end: fmtDate(new Date()) })} className="px-2 py-1.5 text-xs bg-gray-200 hover:bg-gray-300">All</button>
            </div>
            <button
              type="button"
              onClick={exportExcel}
              className="border border-[#0F2C5E] text-[#0F2C5E] px-3 py-1.5 text-sm hover:bg-[#E4F2FF]"
              title="Download the current period as an Excel file"
            >
              Export Excel
            </button>
          </form>
          {rangeError && (
            <p className="text-red-500 text-xs mt-1 text-right">{rangeError}</p>
          )}
        </div>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : data ? (
        <>
          <div className="text-sm text-gray-500">
            Period: {isoToUS(data.start)} — {isoToUS(data.end)}
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <StatCard
              label="Forms filed to IRS"
              value={t.forms_filed ?? 0}
              sub={breakdownStr || 'no filings in period'}
              accent="bg-blue-50 border-blue-200"
            />
            <StatCard
              label="IRS submissions"
              value={t.irs_submissions ?? 0}
              sub="incl. resubmissions"
            />
            <StatCard label="Forms paid" value={t.forms_paid ?? 0} sub="payment completed in period" />
            <StatCard label="Total users (all time)" value={t.users_total ?? 0} />
            <StatCard
              label="New users"
              value={t.new_users ?? 0}
              accent="bg-green-50 border-green-200"
            />
            <StatCard
              label="New users who filed"
              value={t.new_users_filed ?? 0}
              accent="bg-green-50 border-green-200"
            />
            <StatCard
              label="Registered only"
              value={t.new_users_registered_only ?? 0}
              sub="no filings yet"
              accent="bg-yellow-50 border-yellow-200"
            />
          </div>

          {/* Filings by user */}
          <div>
            <h4 className="text-lg font-semibold mb-2">
              Filings by user in period ({t.filers ?? 0})
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Email</th>
                    <th className="px-4 py-2 text-center">Companies</th>
                    <th className="px-4 py-2 text-center">Forms filed</th>
                    <th className="px-4 py-2 text-center">IRS submissions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.filers_detail || []).map((f) => (
                    <tr key={f.id} className="border-b hover:bg-gray-100">
                      <td className="px-4 py-2">{f.email}</td>
                      <td className="px-4 py-2 text-center">{f.companies}</td>
                      <td className="px-4 py-2 text-center font-semibold">{f.forms_filed}</td>
                      <td className="px-4 py-2 text-center">{f.irs_submissions}</td>
                    </tr>
                  ))}
                  {(data.filers_detail || []).length === 0 && (
                    <tr>
                      <td colSpan="4" className="py-4 text-center text-gray-500">
                        No filings in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {(t.filers ?? 0) > 500 && (
              <div className="text-xs text-gray-500 mt-1">
                Showing the top 500 of {t.filers}.
              </div>
            )}
          </div>

          {/* New users detail */}
          <div>
            <h4 className="text-lg font-semibold mb-2">
              New users in period ({t.new_users ?? 0})
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Email</th>
                    <th className="px-4 py-2 text-left">Registered</th>
                    <th className="px-4 py-2 text-center">Companies</th>
                    <th className="px-4 py-2 text-center">Forms filed</th>
                    <th className="px-4 py-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.new_users_detail || []).map((u) => (
                    <tr key={u.id} className="border-b hover:bg-gray-100">
                      <td className="px-4 py-2">{u.email}</td>
                      <td className="px-4 py-2">
                        {fmtDateUS(u.date_joined)}
                      </td>
                      <td className="px-4 py-2 text-center">{u.companies}</td>
                      <td className="px-4 py-2 text-center">{u.forms_filed}</td>
                      <td className="px-4 py-2 text-center">
                        {u.filed ? (
                          <span className="inline-block px-2 py-0.5 text-xs rounded bg-green-100 text-green-800">
                            Filed
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800">
                            Registered only
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(data.new_users_detail || []).length === 0 && (
                    <tr>
                      <td colSpan="5" className="py-4 text-center text-gray-500">
                        No new users in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {t.new_users > 500 && (
              <div className="text-xs text-gray-500 mt-1">
                Showing the latest 500 of {t.new_users}.
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
